import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { config } from "@/shared/lib/config";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { buildRequestEventDatePrefill } from "../../domain/services/request-date-fields";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { CheckboxCard } from "@/shared/ui/FormCheckboxCard";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  CalendarIcon,
  CheckIcon,
  CopyIcon,
  PlusIcon,
  UserRoundIcon,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type { EventDTO, RequestDTO } from "../../domain/contracts";
import {
  detectEventFeaturePreset,
  eventFeaturesFromPreset,
} from "../lib/event-features-ui";
import { serializeEventForm } from "../lib/serialize-event-form";
import {
  useConvertRequestToEvent,
  useEventQuery,
  useRequestQuery,
} from "../api/queries";
import type { EventFormValues } from "../ui/EventForm";
import { EventForm } from "../ui/EventForm";

type EventLookupItem = { id: string; name: string };
type CreateMode = "new" | "copy";

const STATUS_LABELS: Record<string, string> = {
  new: "ໃໝ່",
  negotiating: "ກຳລັງເຈລະຈາ",
  pending_quote: "ລໍຖ້າໃບສະເໜີລາຄາ",
  won: "ຢືນຢັນແລ້ວ",
  lost: "ເສຍໂອກາດ",
  cancelled: "ຍົກເລີກ",
};

function fmtDate(value: Date | string | null | undefined) {
  if (!value) return "-";
  return formatDateTimeLocal(new Date(value));
}

function requestCode(id: string) {
  return `REQ-${id.slice(0, 6).toUpperCase()}`;
}

function computeEndDateInput(
  requestStart: string,
  sourceStart: Date | string,
  sourceEnd: Date | string | null | undefined,
) {
  if (!requestStart || !sourceEnd) return null;
  const durationMs =
    new Date(sourceEnd).getTime() - new Date(sourceStart).getTime();
  if (durationMs <= 0) return null;
  return toDateTimeLocalValue(
    new Date(new Date(requestStart).getTime() + durationMs),
  );
}

function buildEventFormInitialValues(
  request: RequestDTO,
  createMode: CreateMode,
  sourceEvent?: (EventDTO & { assignments?: unknown[] }) | null,
): Partial<EventFormValues> {
  const { eventDate, endDate } = buildRequestEventDatePrefill({
    dateMode: request.dateMode,
    requestedEventDate: request.requestedEventDate,
    showDates: request.showDates,
  });
  const base: Partial<EventFormValues> = {
    title: request.title,
    status: "confirmed",
    categoryId: request.categoryId ?? "",
    featurePreset: "performance_show",
    eventFeatures: eventFeaturesFromPreset("performance_show"),
    eventDate,
    endDate,
    locationText: "",
    locationProvince: null,
    isProvincial: false,
    perDiemPolicyId: null,
    perDiemDays: 1,
    notes: request.notes ?? "",
  };

  if (createMode === "copy" && sourceEvent) {
    const features = sourceEvent.eventFeatures ?? eventFeaturesFromPreset("performance_show");
    return {
      ...base,
      endDate: computeEndDateInput(
        eventDate,
        sourceEvent.eventDate,
        sourceEvent.endDate,
      ),
      locationText: sourceEvent.locationText ?? "",
      locationProvince: sourceEvent.locationProvince ?? null,
      isProvincial: sourceEvent.isProvincial ?? false,
      perDiemPolicyId: sourceEvent.perDiemPolicyId ?? null,
      perDiemDays: sourceEvent.perDiemDays ?? 1,
      notes: sourceEvent.notes ?? request.notes ?? "",
      categoryId: sourceEvent.categoryId ?? "",
      eventFeatures: features,
      featurePreset: detectEventFeaturePreset(features),
    };
  }

  return base;
}

export function RequestConvertPage() {
  const { id } = useParams({ from: "/app/performance/requests/$id/convert" });
  const nav = useNavigate({ from: "/app/performance/requests/$id/convert" });
  const { data: request, isLoading } = useRequestQuery(id);
  const convert = useConvertRequestToEvent(id);
  const [createMode, setCreateMode] = useState<CreateMode>("new");
  const [sourceEventId, setSourceEventId] = useState("");
  const sourceEventQuery = useEventQuery(sourceEventId);
  const submitFormRef = useRef<(() => void) | null>(null);

  const registerSubmit = useCallback((submit: () => void) => {
    submitFormRef.current = submit;
  }, []);

  const handleConvert = async (values: EventFormValues) => {
    if (!request) return;
    if (createMode === "copy" && !sourceEventId) {
      toast.error("ກະລຸນາເລືອກງານສະແດງຕົ້ນແບບ");
      return;
    }
    try {
      const result = await convert.mutateAsync({
        ...serializeEventForm(values),
        priorityOrder: request.priorityOrder,
        ...(createMode === "copy" ? { copyFromEventId: sourceEventId } : {}),
      });
      nav({
        to: "/app/performance/events/$id",
        params: { id: result.event.id },
      });
    } catch {}
  };

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ຄິວຄຳຂໍ", to: "/app/performance/requests" },
              { label: "ກຳລັງໂຫຼດ..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫຼດຄຳຂໍ...</p>
        </Main>
      </>
    );
  }

  if (!request) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ຄິວຄຳຂໍ", to: "/app/performance/requests" },
              { label: "ບໍ່ພົບຂໍ້ມູນ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບຄຳຂໍທີ່ຈະແປງ</p>
        </Main>
      </>
    );
  }

  const formKey =
    createMode === "copy"
      ? `copy-${sourceEventId || "pending"}`
      : `new-${request.id}`;
  const showEventForm =
    createMode === "new" ||
    (createMode === "copy" && !!sourceEventId && !!sourceEventQuery.data);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ຄິວຄຳຂໍ", to: "/app/performance/requests" },
            {
              label: request.title,
              to: "/app/performance/requests/$id",
              params: { id },
            },
            { label: "ແປງເປັນງານສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h2 className="font-bold text-2xl tracking-tight">
            ແປງຄຳຂໍເປັນງານສະແດງ
          </h2>
          <p className="text-muted-foreground">
            ກຳນົດຕາຕະລາງ ແລະ ຂໍ້ມູນການຈັດງານກ່ອນສ້າງ event.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-muted-foreground text-sm">
            <span className="flex items-center gap-2">
              <CalendarIcon className="size-4 shrink-0" />
              ວັນທີລູກຄ້າຕ້ອງການ:{" "}
              {request.dateMode === "show_dates" && (request.showDates?.length ?? 0) > 0
                ? `${request.showDates!.length} ວັນໂຊ (${fmtDate(request.showDates![0]!.showDate)} – ${fmtDate(request.showDates!.at(-1)!.showDate)})`
                : fmtDate(request.requestedEventDate)}
            </span>
            <span className="flex items-center gap-2">
              <UserRoundIcon className="size-4 shrink-0" />
              ຜູ້ຮັບຜິດຊອບ: {request.assignedToName ?? request.assignedTo ?? "-"}
            </span>
            <span className="flex items-center gap-2">
              <CheckIcon className="size-4 shrink-0" />
              ສະຖານະປັດຈຸບັນ: {STATUS_LABELS[request.status] ?? request.status}
            </span>
          </div>
        </div>

        <section className="rounded-xl border bg-card px-6 pt-6">
          <div className="mb-6 space-y-4 border-b pb-6">
              <div>
                <h3 className="font-semibold text-lg">ຕັ້ງຄ່າງານສະແດງ</h3>
                <p className="mt-1 text-muted-foreground text-sm">
                  ເລືອກວິທີສ້າງ ແລະ ກຳນົດຂໍ້ມູນກ່ອນຢືນຢັນ
                </p>
              </div>

              <div className="space-y-3">
                <p className="font-medium text-sm">ວິທີສ້າງງານ</p>
                <Tabs
                  value={createMode}
                  onValueChange={(value) => {
                    const mode = value as CreateMode;
                    setCreateMode(mode);
                    if (mode === "new") setSourceEventId("");
                  }}
                >
                  <TabsList className="w-full justify-start sm:w-auto">
                    <TabsTrigger value="new" className="gap-2">
                      <PlusIcon className="size-4" />
                      ສ້າງໃໝ່
                    </TabsTrigger>
                    <TabsTrigger value="copy" className="gap-2">
                      <CopyIcon className="size-4" />
                      ຄັດລອກຈາກງານກ່ອນ
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {createMode === "new" ? (
                  <p className="text-muted-foreground text-sm">
                    ສ້າງງານໃໝ່ຈາກຂໍ້ມູນຄຳຂໍ ໂດຍບໍ່ດຶງຂໍ້ມູນຈາກງານເກົ່າ
                  </p>
                ) : (
                  <div className="space-y-2 rounded-lg border bg-muted/20 p-4">
                    <p className="font-medium text-sm">ເລືອກງານສະແດງຕົ້ນແບບ</p>
                    <InfiniteCombobox<EventLookupItem>
                      queryKey={["performance-events", "lookup"]}
                      queryFn={({ search, pageParam }) =>
                        fetchLookupForInfinite<EventLookupItem>(
                          `${config.apiUrl}/events/lookup`,
                          { search, pageParam },
                        )
                      }
                      preloadQueryFn={(eventId) =>
                        hydrateLookupItem<EventLookupItem>(
                          `${config.apiUrl}/events/lookup`,
                          eventId,
                        ).then((item) => item ?? null)
                      }
                      getLabel={(item) => item.name}
                      getValue={(item) => item.id}
                      value={sourceEventId}
                      onValueChange={setSourceEventId}
                      placeholder="ຄົ້ນຫາງານສະແດງ..."
                      className="w-full"
                      clearable
                    />
                    {sourceEventQuery.isLoading && sourceEventId ? (
                      <p className="text-muted-foreground text-sm">
                        ກຳລັງໂຫຼດຂໍ້ມູນງານຕົ້ນແບບ...
                      </p>
                    ) : sourceEventQuery.data ? (
                      <p className="text-muted-foreground text-sm">
                        ຈະຄັດລອກ{" "}
                        <span className="font-medium text-foreground">
                          {sourceEventQuery.data.assignments?.length ?? 0}
                        </span>{" "}
                        ການຈັດນັກສະແດງ · ດຶງ category/features ຈາກງານນີ້
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        ເລືອກງານເພື່ອດຶງຂໍ້ມູນສະຖານທີ່ ແລະ ການຈັດນັກສະແດງ
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {showEventForm ? (
              <EventForm
                key={formKey}
                embedded
                hideFooter
                registerSubmit={registerSubmit}
                initialValues={buildEventFormInitialValues(
                  request,
                  createMode,
                  sourceEventQuery.data,
                )}
                onSubmit={handleConvert}
                submitting={convert.isPending}
              />
            ) : createMode === "copy" ? (
              <p className="rounded-lg border border-dashed bg-muted/20 p-6 text-center text-muted-foreground text-sm">
                ເລືອກງານຕົ້ນແບບກ່ອນ ເພື່ອຕັ້ງຄ່າ category ແລະ features
              </p>
            ) : null}

            <CheckboxCard
              checked
              disabled
              label={`ຜູກງານນີ້ກັບຄຳຂໍ #${requestCode(request.id)}`}
              description="ສ້າງງານຈາກຄຳຂໍນີ້ໂດຍອັດຕະໂນມັດ"
              className="mt-4"
            />

            <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                disabled={convert.isPending}
                onClick={() =>
                  nav({
                    to: "/app/performance/requests/$id",
                    params: { id },
                  })
                }
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="button"
                disabled={
                  convert.isPending ||
                  !showEventForm ||
                  (createMode === "copy" &&
                    (!sourceEventId || sourceEventQuery.isLoading))
                }
                onClick={() => submitFormRef.current?.()}
              >
                {convert.isPending
                  ? "ກຳລັງສ້າງ..."
                  : createMode === "copy"
                    ? "ສ້າງງານ (ຄັດລອກ)"
                    : "ສ້າງງານ"}
                <ArrowRightIcon className="size-4" />
              </Button>
            </div>
          </section>
      </Main>
    </>
  );
}
